import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import AdminLayout from "../../../layouts/AdminLayout";
import Button from "../../../components/ui/Button";
import FormField from "../../../components/ui/FormField";
import TextInput from "../../../components/ui/TextInput";
import Textarea from "../../../components/ui/Textarea";
import { createSupportTicket } from "../../../services/supportTicketService";
import { supportTicketSchema, zodErrorToFieldErrors } from "../../../schemas/formSchemas";

const CreateSupportTicket = () => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ subject: "", description: "" });

  const errors = useMemo(() => {
    const parsed = supportTicketSchema.safeParse(form);
    return parsed.success ? {} : zodErrorToFieldErrors(parsed.error);
  }, [form]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsed = supportTicketSchema.safeParse(form);
    if (!parsed.success) {
      toast.error("Please fix the errors and try again.");
      return;
    }

    setBusy(true);
    try {
      const payload = await createSupportTicket(parsed.data);
      if (payload?.success) {
        toast.success("Support ticket created.");
        navigate("/resident/support-tickets");
      } else {
        toast.error(payload?.message || "Unable to create support ticket.");
      }
    } catch (err) {
      toast.error(err?.message || "Unable to create support ticket.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-2xl mx-auto bg-white rounded shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Create Support Ticket</h2>
          <Button variant="outline" onClick={() => navigate("/resident/support-tickets")}>
            Back
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField label="Subject" required error={errors.subject}>
            <TextInput
              name="subject"
              value={form.subject}
              onChange={onChange}
              placeholder="e.g., Gym equipment not working"
              required
              maxLength={255}
              error={Boolean(errors.subject)}
            />
          </FormField>

          <FormField label="Description" hint="Describe the issue, location, or complaint">
            <Textarea
              name="description"
              value={form.description}
              onChange={onChange}
              placeholder="Describe the issue in detail..."
              rows={5}
            />
          </FormField>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" type="button" onClick={() => navigate("/resident/support-tickets")}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy} loading={busy}>
              Submit Ticket
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default CreateSupportTicket;
